/**
 * @module app.shared
 */

import { autoinject, Disposable } from 'aurelia-framework';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { compressToUTF16, decompressFromUTF16 } from 'lz-string';
import { EventAggregator } from 'aurelia-event-aggregator';

export type StorageArea = 'local' | 'session';

const areas = {
    'local': window.localStorage,
    'session': window.sessionStorage
};

type TypedEventListener<E extends Event> = (e: E) => void;

export interface StorageCallbackEvent<T> {
    area: StorageArea;
    key: string;
    oldValue: T | undefined;
    newValue: T | undefined;
}

export type StorageCallback<T> = (e: StorageCallbackEvent<T>) => void;

export interface IStorageSubscription extends Disposable {
    area: StorageArea;
    key: string;
}

// interface StorageSyncState<T> {
//     key: string;
//     value: T | undefined;
// }

//const SYNC_KEY = '[sync]';

export class StorageService {

    constructor(private readonly clientStorage: ClientStorage) {
        //this.clientStorage.subscribe<StorageSyncState<unknown>>(SYNC_KEY, e => this.processSessionSync(e));
    }

    public getValue(scope: string, key: string): unknown {
        const settings = this.clientStorage.getObject(scope);
        if(!settings) { return undefined; }

        return settings[key];
    }

    public setValue(scope: string, key: string, value: unknown) {
        const settings = this.clientStorage.getObject(scope) || {};
        if(value === undefined) {
            delete settings[key];
        }
        else {
            settings[key] = value;
        }
        this.clientStorage.setObject(scope, settings);
    }

    // public synchronizeSession(key: string, value: unknown) {
    //     const state = { syncStorage: { key, value } };
    //     this.clientStorage.setObject(SYNC_KEY, { key, value });
    //     this.clientStorage.remove(SYNC_KEY);
    // }

    // private processSessionSync(e: StorageCallbackEvent<StorageSyncState<unknown>>) {
    //     if(e.newValue) {
    //         this.clientStorage.setObject(e.newValue.key, e.newValue.value, 'session');
    //     }
    // }

}

class StorageSubscription<T> implements IStorageSubscription {

    constructor(
        private readonly clientStorage: ClientStorage,
        public readonly area: StorageArea,
        public readonly key: string,
        public readonly callback: StorageCallback<T>) { }

    public dispose(): void {
        this.clientStorage.unsubscribe(this);
    }

}

/**
 * Provides automatic application instance scoping/serialization/compression when
 * storing to `localStorage`/`sessionStorage`.
 */
export class ClientStorage {

    private readonly prefix: string;
    private readonly subscriptions: Map<string, StorageSubscription<any>[]>;
    private eventListener: TypedEventListener<StorageEvent> | null = null;

    constructor(public readonly instanceScope: string) {
        if(!instanceScope) {
            console.warn('ClientStorage instances should not be injected or created manually.')
        }
        const scope = instanceScope;
        this.prefix = scope ? `${scope}.` : '';

        this.subscriptions = new Map<string, StorageSubscription<any>[]>();
    }

    public subscribe<T>(key: string, callback: StorageCallback<T>, area: StorageArea = 'local'): IStorageSubscription {
        if(this.eventListener === null) {
            // register global storage event handler on window
            this.eventListener = e => this.handleStorageEvent(e);
            window.addEventListener('storage', this.eventListener);
        }

        const fullKey = this.getFullKey(key);
        const subList = this.subscriptions.get(fullKey) || [];
        const subscription = new StorageSubscription(this, area, key, callback);
        subList.push(subscription);
        this.subscriptions.set(fullKey, subList);

        return subscription;
    }

    public unsubscribe(subscription: IStorageSubscription): void {
        const fullKey = this.getFullKey(subscription.key);
        let subList = this.subscriptions.get(fullKey);
        if(subList) {
            subList = subList.filter(sub => sub !== subscription);
            if(subList.length === 0) {
                this.subscriptions.delete(fullKey);
            }
            else {
                this.subscriptions.set(fullKey, subList);
            }
        }
    }

    private handleStorageEvent(e: StorageEvent): void {
        if(!e.key) { return; }
        //const area = e.storageArea === window.localStorage ? 'LOCAL' : 'SESSION';
        //const oldLength = e.oldValue && `${e.oldValue.length}` || '(none)';
        //const newLength = e.oldValue && `${e.oldValue.length}` || '(none)';
        //console.log(`Storage Event [${area}] ${e.key} ${oldLength}->${newLength} characters.`);

        const subList = this.subscriptions.get(e.key);
        if(subList) {
            for(const subscription of subList) {
                if(subscription && areas[subscription.area] === e.storageArea) {
                    subscription.callback({
                        key: subscription.key,
                        area: subscription.area,
                        oldValue: this.parse(e.oldValue),
                        newValue: this.parse(e.newValue)
                    });
                }
            }
        }
    }

    private parse(value: string | null): unknown | undefined {
        if(!value) { return undefined; }
        else {
            let decompressedValue = decompressFromUTF16(value);
            return decompressedValue ? JSON.parse(<string>decompressedValue) : undefined;
        }
    }

    public getFullKey(key: string): string {
        return `${this.prefix}${key}`;
    }

    public remove(key: string, area: StorageArea = 'local'): void {
        const storage = areas[area];
        storage.removeItem(this.getFullKey(key));
    }

    private getString(key: string, area: StorageArea = 'local'): string | null {
        const storage = areas[area];
        const result = storage.getItem(this.getFullKey(key));
        if(result === null) {
            return null;
        }
        return decompressFromUTF16(result);
    }

    public getObject<T = any>(key: string, area: StorageArea = 'local'): T | null {
        const result = this.getString(key, area);
        if(result === null) {
            return null;
        }
        return JSON.parse(result);
    }

    private setString(key: string, value: string | null, area: StorageArea = 'local'): void {
        const storage = areas[area];
        const fullKey = this.getFullKey(key);
        if(value === null || value === undefined) {
            storage.removeItem(fullKey);
        }
        else {
            const compressed = compressToUTF16(value);
            storage.setItem(fullKey, compressed);
        }
    }

    public setObject(key: string, value: unknown, area: StorageArea = 'local'): void {
        if(value === undefined || value === null) {
            this.setString(key, null, area);
        }
        else {
            this.setString(key, JSON.stringify(value), area);
        }
    }

    public getKeys(area: StorageArea = 'local'): string[] {
        const storage = areas[area];
        const keys: string[] = [];
        for(var k = 0; k < storage.length; k++) {
            const key = storage.key(k);
            if(key && key.startsWith(this.prefix)) {
                keys.push(key.substr(this.prefix.length));
            }
        };
        return keys;
    }

}
